/**
 * tailwindClassnames.tsx
 * 
 * This file contains reusable Tailwind CSS class combinations for consistent styling across the application.
 */

// Layout and Container Classes
export const containerClasses = {
    page: "bg-very-light-gray transition-all duration-300",
    content: "w-full max-w-content mx-auto",
    card: "bg-white shadow rounded-md p-4 transition-all duration-300",
    header: "bg-white shadow static inset-x-0 px-8 desktop:px-[60px] pt-5 -mt-5 -mx-8 desktop:-mx-[60px] transition-all duration-300"
};

// Text Classes
export const textClasses = {
    heading: "text-4xl font-medium mb-3 text-off-black",
    subheading: "text-xl font-semibold text-off-black",
    body: "text-off-black",
    label: "text-sm font-medium text-neutral-gray-900"
};

// Button Classes
export const buttonClasses = {
    // Canonical primary action button, including its disabled state (design
    // "Disabled" token #767676 text on the same token at 30% background).
    // Single source of truth — call sites toggle the `disabled` prop
    // instead of swapping classes, and add only layout extras (margins, sizing).
    primary: "bg-dark-green enabled:hover:bg-status-green text-white font-bold rounded px-7 py-2 disabled:bg-ui-disabled/30 disabled:text-ui-disabled disabled:cursor-not-allowed transition-colors duration-300",
    danger: "bg-status-red hover:bg-red-700 text-white font-bold rounded px-7 py-2 transition-colors duration-300",
    destructive: "bg-orange hover:bg-light-orange text-white font-bold rounded px-7 py-2 transition-colors duration-300",
    link: "text-blue-basic hover:text-blue-700 underline transition-colors duration-300",
    // Canonical secondary positive-action button (add buttons, Save and Close,
    // Clear All Filters, Test Connection, etc.). Single source of truth per
    // design: active = status-green @ 8% fill with dark-green text; hover =
    // #F3F3F3 fill with black text; disabled = #F3F3F3 fill with the Disabled
    // token at 50% text. Call sites add only layout extras (margins, w-fit,
    // whitespace-nowrap) — never their own colors/padding.
    lightGreen: "bg-status-green/[0.08] text-dark-green enabled:hover:bg-light-gray-button enabled:hover:text-grey-g0 font-semibold rounded py-2 px-4 disabled:bg-light-gray-button disabled:text-ui-disabled/50 disabled:cursor-not-allowed transition-colors duration-300",
    // Canonical destructive-membership button (Remove member, Leave workspace):
    // red text (#D0031B) on a pale red fill (#FDF2F4). Hover is not in the design
    // spec, so it darkens the fill one step (#FBE3E6). Single source of truth —
    // call sites toggle the `disabled` prop and add only layout extras (margins).
    lightRed: "bg-remove-red-bg text-remove-red rounded px-3 py-1 text-sm enabled:hover:bg-remove-red-bg-hover disabled:opacity-40 disabled:cursor-not-allowed transition-colors duration-200",
    // Canonical Cancel / secondary text button. No fill, no outline (per style guide).
    // Single source of truth for every Cancel button across the app. Call sites add
    // only layout extras (margins) — never their own colors/padding.
    cancel: "text-secondary-gray enabled:hover:text-medium-gray disabled:text-gray-lines disabled:cursor-not-allowed font-bold rounded px-4 py-2 transition-colors duration-300"
};

// Segmented toggle (e.g. Code/Builder, View/Edit). Single source of truth for the
// toggle look — change the selected color here and every toggle updates. Consumed
// via the <SegmentedToggle> component.
export const toggleClasses = {
    container: "flex rounded border border-field-outline overflow-hidden",
    option: "text-sm font-semibold px-3 py-1.5 transition-colors",
    active: "bg-[#0156C7] text-white",
    inactive: "text-secondary-gray bg-transparent hover:text-off-black",
    divider: "border-l border-field-outline",
};

// Table Classes
export const tableClasses = {
    container: "table-auto h-auto w-full border border-gray-lines text-off-black rounded-md bg-very-light-gray transition-all duration-300",
    fixedContainer: "table-fixed h-auto w-full border border-gray-lines text-off-black rounded-md bg-very-light-gray transition-all duration-300",
    header: "px-3 pt-3 pb-2 font-semibold text-base bg-light-gray text-left transition-all duration-300",
    headerCell: "p-3",
    row: "bg-very-light-gray border border-gray-lines hover:bg-light-blue align-top transition-all duration-300",
    cell: "p-3",
    sortIcon: "cursor-pointer",

    // Status styles
    statusHealthy: "flex items-center text-dark-green",
    statusWarning: "flex items-center text-status-orange",
    statusError: "flex items-center text-status-red",
};

// Filter and Search Classes
export const filterClasses = {
    container: "text-off-black bg-very-light-gray mr-3 mb-3 h-auto px-3 pb-3 divide-y divide-gray-lines overflow-y-auto overflow-x-hidden scrollbar-thin scrollbar-thumb-neutral-gray-400 scrollbar-track-transparent hover:scrollbar-thumb-neutral-gray-500 transition-all duration-300 w-80 flex-shrink-0",
    section: "my-6 pt-3",
    tag: "text-sm w-fit py-1 px-2 bg-light-green mt-1 rounded-sm items-end",
    tagCount: "bg-dark-green text-white text-sm py-1 px-3 rounded-full",
    header: "flex flex-row justify-between mt-2 pt-2",
    label: "text-sm font-normal text-off-black",
};

// Pagination Classes
export const paginationClasses = {
    container: "w-full mt-2 flex flex-row justify-between bg-very-light-gray opacity-80 text-base"
};

// Form Classes
export const formClasses = {
    group: "mb-4",
    label: "block text-sm font-medium text-off-black mb-1",
    input: "w-full p-2 border border-gray-lines rounded focus:outline-none focus:ring-2 focus:ring-dark-green",
    select: "w-full p-2 border border-gray-lines rounded focus:outline-none focus:ring-2 focus:ring-dark-green",
    checkbox: "mr-2 rounded text-dark-green focus:ring-dark-green",
    error: "mt-1 text-sm text-status-red"
};

// Modal Classes
export const modalClasses = {
    overlay: "fixed inset-0 bg-dark-gray opacity-80 flex justify-center items-center z-50 transition-colors duration-300",
    container: "bg-white rounded-md p-6 max-w-lg w-full shadow-xl",
    header: "text-xl font-bold mb-4 text-off-black",
    body: "mb-6 text-off-black",
    footer: "flex justify-end space-x-2"
};

// Status and Notification Classes
export const statusClasses = {
    success: "text-dark-green",
    warning: "text-status-orange",
    error: "text-status-red",
    info: "text-blue-basic"
};

// Radio Select List Classes
export const radioSelectClasses = {
    container: "flex flex-col max-w-xs",
    header: "w-full flex flex-row justify-between",
    sectionLabel: "font-semibold text-off-black",
    input: "my-2 p-1.5 border border-gray-lines rounded rounded-sm px-2 pr-8 w-full transition-all duration-300",
    clearButton: "absolute right-1.5 top-1/2 transform -translate-y-1/2 text-dark-green",
    listContainer: "max-h-40 overflow-auto scrollbar-thin scrollbar-thumb-neutral-gray-400 scrollbar-track-transparent hover:scrollbar-thumb-neutral-gray-500",
    itemContainer: "flex flex-row",
    radioInput: "accent-dark-green cursor-pointer",
    radioLabel: "inline-block mx-2 text-off-black truncate",
    radioLabelHover: "inline-block hover:cursor-pointer mx-2 text-off-black truncate",
    loadMoreLabel: "text-dark-green text-sm cursor-pointer",
    loadingLabel: "text-medium-gray text-sm cursor-pointer"
};

// Select List Classes (for checkboxes)
export const selectListClasses = {
    container: "flex flex-col max-w-xs",
    header: "w-full flex flex-row justify-between",
    sectionLabel: "font-semibold text-off-black",
    input: "my-2 p-1.5 border border-gray-lines rounded rounded-sm px-2 pr-8 w-full transition-all duration-300",
    clearButton: "absolute right-1.5 top-1/2 transform -translate-y-1/2 text-dark-green",
    listContainer: "max-h-40 overflow-auto scrollbar-thin scrollbar-thumb-neutral-gray-400 scrollbar-track-transparent hover:scrollbar-thumb-neutral-gray-500",
    itemContainer: "flex flex-row",
    checkboxInput: "accent-dark-green cursor-pointer",
    checkboxLabel: "inline-block mx-2 text-off-black truncate",
    checkboxLabelHover: "inline-block hover:cursor-pointer mx-2 text-off-black truncate",
    loadMoreLabel: "text-dark-green text-sm cursor-pointer",
    loadingLabel: "text-medium-gray text-sm cursor-pointer"
};

// Connection Status Classes
// Shared pill layout used by connection status badges AND the detail-page header info pills.
// flex, 24px tall, 6px/3px padding, 4px gap, 3px radius, 15px/normal. Colors (bg/text) supplied per use.
const statusPillBase = "flex items-center gap-1 h-6 px-[6px] py-[3px] rounded-[3px] text-[15px] font-normal leading-normal transition-all duration-300";
// List badge: compact, centered.
const statusListBase = "text-xs text-center rounded rounded-sm transition-all duration-300";
// Compose a status style: same shape everywhere, only the colors change.
const statusStyle = (colors: string) => (detailsPage: boolean) =>
    detailsPage ? `${statusPillBase} ${colors}` : `${statusListBase} ${colors}`;

export const connectionStatusClasses = {
    // Specific status styles — shape is shared, only colors differ
    healthy: statusStyle("bg-light-green text-dark-green"),
    unhealthy: statusStyle("bg-very-light-orange text-orange"),
    needsReview: statusStyle("bg-warning-yellow text-warning-dark-yellow"),
    disabled: statusStyle("bg-light-gray text-medium-gray"),
    unknown: statusStyle("bg-light-gray text-medium-gray"),
};

// Connection Details Classes
export const connectionDetailsClasses = {
    container: "relative h-full",
    header: "bg-white shadow static inset-x-0 px-8 desktop:px-[60px] pt-5 -mt-5 -mx-8 desktop:-mx-[60px] transition-all duration-300",
    title: "text-3xl font-semibold text-off-black",
    infoLabel: "text-secondary-gray mt-2 mb-3 mr-1 text-sm",
    infoPill: "text-secondary-gray mt-2 mb-3 bg-light-gray rounded rounded-sm px-2 py-1 mr-3 text-sm",
    // Header info pill: same pill shape as the status badges, grey colors.
    headerPill: `${statusPillBase} bg-light-gray-button text-secondary-text`,
    separator: "text-secondary-gray mt-2 mb-3 mr-2",
    enabledPanel: "flex flex-row items-center px-2 py-3 gap-4 w-fit border border-[#EBF4EB] rounded transition-all duration-300",
    disabledPanel: "flex flex-row items-center px-2 py-3 gap-4 w-fit border border-gray-lines rounded transition-all duration-300",
    tabActive: "border-2 border-white border-b-status-green text-dark-gray mr-3 py-3 cursor-pointer transition-all duration-300",
    tabInactive: "border-2 border-white mr-3 py-3 cursor-pointer text-medium-gray transition-all duration-300",
    switchText: "font-semibold text-off-black flex flex-row items-center",
    switchTextLabel: "mr-2 whitespace-nowrap",
    switchContainer: "group relative flex h-7 w-14 cursor-pointer rounded-full p-1 transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 data-[focus]:outline-1 data-[focus]:outline-white"
};

// Skeleton Loading Classes
export const skeletonClasses = {
    base: "bg-neutral-gray-50 rounded animate-pulse",
};

// Text Input Classes
export const inputClasses = {
    standard: "p-1.5 h-auto border border-neutral-gray-200 rounded text-off-black transition-all duration-300",
    disabled: "p-1.5 h-auto border border-neutral-gray-200 rounded bg-light-gray text-medium-gray transition-all duration-300",
    error: "p-1.5 h-auto border border-orange rounded text-off-black transition-all duration-300"
};

// Global Search Classes
export const globalSearchClasses = {
    container: "flex flex-col max-w-xs items-start transition-all duration-300",
    inputContainer: "flex flex-row",
    inputWrapper: "relative",
    input: "p-1.5 border border-neutral-gray-200 rounded rounded-sm px-2 pr-8 transition-all duration-300",
    clearButton: "absolute right-0 top-0 mt-1.5 mr-1 text-dark-green transition-all duration-300",
    searchButton: "mx-2 mb-2 bg-dark-green hover:bg-status-green text-white rounded text-sm p-2 h-auto transition-all duration-300"
};/**
 * Helper function to combine class names
 * @param classes - Array of class names
 * @returns Combined class names string
 */
export const combineClasses = (...classes: (string | undefined | null | false)[]): string => {
    return classes.filter(Boolean).join(' ');
};

// Example usage of combineClasses:
// const tableHeaderClass = combineClasses(tableClasses.headerCell, sortable && tableClasses.sortIcon);
